import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const content = {
  en: {
    title: "Privacy Policy",
    back: "Back to Home",
    sections: [
      {
        heading: "1. Information We Collect",
        body: 'SolVida Financial ("we," "our," or "us") collects information that you provide directly to us when you:',
        list: ["Fill out our contact form", "Request a quote or consultation", "Call or email us", "Subscribe to our newsletter", "Interact with our website"],
        after: "The types of information we may collect include your name, email address, phone number, state of residence, and any other information you choose to provide in your message to us."
      },
      {
        heading: "2. How We Use Your Information",
        body: "We use the information we collect to:",
        list: ["Respond to your inquiries and provide customer service", "Process your insurance applications and requests", "Communicate with you about our services and insurance products", "Comply with legal obligations and regulatory requirements", "Improve our website and services", "Send you marketing communications (with your consent)"]
      },
      {
        heading: "3. Information Sharing",
        body: "We may share your information with:",
        list: ["Insurance Carriers: To obtain quotes and process applications", "Service Providers: Who assist us in operating our business (CRM, email services)", "Legal Authorities: When required by law or to protect our rights"],
        after: "We do not sell your personal information to third parties."
      },
      {
        heading: "4. Cookies and Tracking",
        body: "Our website may use cookies and similar tracking technologies to enhance your experience. You can control cookies through your browser settings. We may use Google Analytics or similar services to understand how visitors use our site."
      },
      {
        heading: "5. Data Security",
        body: "We implement reasonable security measures to protect your personal information. However, no method of transmission over the internet is 100% secure. We use SSL encryption for data transmission and store your information securely."
      },
      {
        heading: "6. Your Rights",
        body: "You have the right to:",
        list: ["Access the personal information we hold about you", "Request correction of inaccurate information", "Request deletion of your information (subject to legal requirements)", "Opt-out of marketing communications", "File a complaint with relevant data protection authorities"]
      },
      {
        heading: "7. Children's Privacy",
        body: "Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children."
      },
      {
        heading: "8. Changes to This Policy",
        body: "We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the effective date."
      },
      {
        heading: "9. Contact Us",
        body: "If you have any questions about this Privacy Policy, please contact us:"
      }
    ]
  },
  es: {
    title: "Política de Privacidad",
    back: "Volver al Inicio",
    sections: [
      {
        heading: "1. Información que Recopilamos",
        body: 'SolVida Financial ("nosotros" o "nuestro") recopila información que usted nos proporciona directamente cuando:',
        list: ["Llena nuestro formulario de contacto", "Solicita una cotización o consulta", "Nos llama o envía un correo electrónico", "Se suscribe a nuestro boletín informativo", "Interactúa con nuestro sitio web"],
        after: "Los tipos de información que podemos recopilar incluyen su nombre, dirección de correo electrónico, número de teléfono, estado de residencia y cualquier otra información que elija proporcionar en su mensaje."
      },
      {
        heading: "2. Cómo Usamos su Información",
        body: "Usamos la información que recopilamos para:",
        list: ["Responder a sus consultas y brindar servicio al cliente", "Procesar sus solicitudes y aplicaciones de seguros", "Comunicarnos con usted sobre nuestros servicios y productos de seguros", "Cumplir con obligaciones legales y requisitos regulatorios", "Mejorar nuestro sitio web y servicios", "Enviarle comunicaciones de marketing (con su consentimiento)"]
      },
      {
        heading: "3. Compartir Información",
        body: "Podemos compartir su información con:",
        list: ["Compañías Aseguradoras: Para obtener cotizaciones y procesar solicitudes", "Proveedores de Servicios: Que nos ayudan a operar nuestro negocio (CRM, servicios de correo electrónico)", "Autoridades Legales: Cuando la ley lo requiera o para proteger nuestros derechos"],
        after: "No vendemos su información personal a terceros."
      },
      {
        heading: "4. Cookies y Seguimiento",
        body: "Nuestro sitio web puede utilizar cookies y tecnologías de seguimiento similares para mejorar su experiencia. Puede controlar las cookies a través de la configuración de su navegador. Podemos utilizar Google Analytics o servicios similares para comprender cómo los visitantes usan nuestro sitio."
      },
      {
        heading: "5. Seguridad de Datos",
        body: "Implementamos medidas de seguridad razonables para proteger su información personal. Sin embargo, ningún método de transmisión por internet es 100% seguro. Utilizamos cifrado SSL para la transmisión de datos y almacenamos su información de manera segura."
      },
      {
        heading: "6. Sus Derechos",
        body: "Usted tiene derecho a:",
        list: ["Acceder a la información personal que tenemos sobre usted", "Solicitar la corrección de información inexacta", "Solicitar la eliminación de su información (sujeto a requisitos legales)", "Optar por no recibir comunicaciones de marketing", "Presentar una queja ante las autoridades de protección de datos correspondientes"]
      },
      {
        heading: "7. Privacidad de Menores",
        body: "Nuestros servicios no están dirigidos a personas menores de 18 años. No recopilamos intencionalmente información personal de menores."
      },
      {
        heading: "8. Cambios a Esta Política",
        body: "Podemos actualizar esta Política de Privacidad de vez en cuando. Le notificaremos de cualquier cambio publicando la nueva Política de Privacidad en esta página y actualizando la fecha de vigencia."
      },
      {
        heading: "9. Contáctenos",
        body: "Si tiene alguna pregunta sobre esta Política de Privacidad, por favor contáctenos:"
      }
    ]
  }
};

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const c = content[lang] || content.en;
  const dateFmt = lang === 'es' ? 'es-CO' : 'en-US';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-20 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-4xl">
        <Button onClick={() => navigate('/')} variant="outline" className="mb-8 flex items-center gap-2" data-testid="privacy-back-btn">
          <ArrowLeft size={20} />
          {c.back}
        </Button>

        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-6" data-testid="privacy-title">{c.title}</h1>
          <p className="text-sm text-slate-600 mb-8">
            {lang === 'es' ? 'Fecha de vigencia: ' : 'Effective Date: '}
            {new Date().toLocaleDateString(dateFmt, { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>

          <div className="prose prose-slate max-w-none space-y-6">
            {c.sections.map((s) => (
              <section key={s.heading}>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">{s.heading}</h2>
                <p className="text-slate-700 leading-relaxed mb-4">{s.body}</p>
                {s.list && (
                  <ul className="list-disc pl-6 space-y-2 text-slate-700">
                    {s.list.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                )}
                {s.after && <p className="text-slate-700 leading-relaxed mt-4">{s.after}</p>}
              </section>
            ))}
            <div className="bg-slate-50 p-6 rounded-lg">
              <p className="text-slate-700"><strong>SolVida Financial</strong></p>
              <p className="text-slate-700">Email: ivonne@solvidafinancial.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
